import { useCallback, useEffect, useMemo, useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import _ from 'lodash';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
} from '@material-ui/core';

function SaveFiltersDialog({ open, state, onCancel, onSave }) {
    const [name, setName] = useState(state.name);
    const [isDefault, setIsDefault] = useState(state.default);
    const nameValid = useMemo(() => {
        return !_.isEmpty(name);
    }, [name]);

    useEffect(() => {
        setName(state.name);
        setIsDefault(state.default);
    }, [state]);

    function handleSave() {
        onSave({
            ...state,
            name,
            default: isDefault,
        });
    }

    return (
        <Dialog id="save-filters-dialog" fullWidth open={open} onClose={onCancel}>
            <DialogTitle>
                <Trans>Save Filters</Trans>
            </DialogTitle>
            <DialogContent>
                <TextField
                    id="saved-filters-name"
                    label={<Trans>Name</Trans>}
                    value={name}
                    fullWidth
                    required
                    error={!nameValid}
                    helperText={!nameValid ? i18n._(t`A name is required`) : undefined}
                    onChange={(e) => setName(e.target.value)}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
                <FormControlLabel
                    control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
                    label={<Trans>Default</Trans>}
                />
            </DialogContent>
            <DialogActions id="save-filters-dialog-actions">
                <Button id="save-filters-dialog-action-cancel" onClick={onCancel}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button id="save-filters-dialog-action-save" disabled={!nameValid} onClick={handleSave}>
                    <Trans>Save</Trans>
                </Button>
            </DialogActions>
        </Dialog>
    );
}

SaveFiltersDialog.propTypes = {
    state: shape({
        name: string.isRequired,
        default: bool,
        filterState: object,
        filterStateSummary: object,
    }).isRequired,
    open: bool.isRequired,
    onCancel: func,
    onSave: func,
};

SaveFiltersDialog.defaultProps = {
    onCancel: _.noop,
    onSave: _.noop,
};

export default SaveFiltersDialog;

function makeNameUnique(savedFilterStates, nameToMakeUnique) {
    let uniqueName = nameToMakeUnique;
    if (_.find(savedFilterStates, (savedState) => savedState.name === uniqueName)) {
        const titleCountRegex = new RegExp('(.+?)(?:\\((\\d+)\\))');
        const match = titleCountRegex.exec(uniqueName);
        let countSuffix = ` (1)`;

        if (match) {
            const count = parseInt(match[2], 10);
            countSuffix = ` (${count + 1})`;
            uniqueName = match[1].trim();
        }

        return makeNameUnique(savedFilterStates, `${uniqueName}${countSuffix}`);
    }

    return uniqueName;
}

export function makeSavedFiltersName(filterState) {
    return _.map(filterState?.summarized, (summary) => summary.shortValue).join(', ');
}

export function useSaveFiltersDialog(filterState, savedFilterStates) {
    const [open, setOpen] = useState(false);
    const [callbacks, setCallbacks] = useState({
        onSave: _.noop,
    });
    const state = useMemo(() => {
        return {
            name: makeNameUnique(savedFilterStates, makeSavedFiltersName(filterState)),
            default: false,
            encodedFilterState: filterState?.encoded,
        };
    }, [filterState, savedFilterStates]);
    const showDialog = useCallback((onSave) => {
        setCallbacks({
            onSave: (stateToSave) => {
                setOpen(false);
                onSave(stateToSave);
            },
        });
        setOpen(true);
    }, []);

    const dialog = (
        <SaveFiltersDialog open={open} state={state} onCancel={() => setOpen(false)} onSave={callbacks.onSave} />
    );

    return [dialog, showDialog];
}
